import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import DataTable from "react-data-table-component";
import { userRequest } from "../utils/requestMethods";
import {
  FiEdit2,
  FiMoreHorizontal,
  FiTrash2,
  FiSquare,
  FiUser,
  FiCreditCard,
  FiPlusCircle,
  FiFileText,
  FiCheckCircle,
  FiAlertCircle,
  FiShield,
} from "react-icons/fi";
import {
  ButtonLoading,
  Header,
  CustomPagination,
  StatusBadge,
  BackButton,
  AddStudentDetailsModal,
  SkeletonTable,
  TableDropdown,
  GenerateInvoicesModal,
  StudentIDModal,
  StudentPromotionModal,
  GenerateQRCodeModal,
  ClassTransferModal,
  StudentLoginAccessModal,
} from "../components";
import { useStateContext } from "../contexts/ContextProvider";
import { useSubscription } from "../contexts/SubscriptionContext";
import * as Icons from "../data/class";
import { MdArrowCircleUp, MdSearch, MdSwapVert } from "react-icons/md";
import { exportStudentsToExcel } from "../utils/exportToExcel";
import { formatCurrency } from "../utils/currencyUtils";
import { getClassesAhead } from "../utils/classHierarchy";
import { isBypassActive } from "../utils/paymentThresholdUtils";

const customStyles = {
  headRow: {
    style: {
      backgroundColor: "rgb(248, 250, 252)",
      fontWeight: "bold",
    },
  },
  hidden: {
    style: {
      display: "none",
    },
  },
};

const Students = ({ history }) => {
  const location = useLocation();
  const classId = location.pathname.split("/")[2];
  //console.log(classId)
  const { school, user } = useStateContext();
  const { refreshSubscription } = useSubscription();
  const schoolId = school?._id;
  const schoolCode = school?.code;
  const hasFinancial = school?.services?.includes("financial_management");
  const isAcademicAdmin = user?.role === 3; // school management only, no invoicing
  const [students, setStudents] = useState([]);
  const [activeStudents, setActiveStudents] = useState(0);
  const [inactiveStudents, setInactiveStudents] = useState(0);
  const [classInfo, setClassInfo] = useState({});
  //const [clicked, setClicked] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
  const [invoiceTargetStudent, setInvoiceTargetStudent] = useState(null);
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [isStudentIDModalOpen, setIsStudentIDModalOpen] = useState(false);
  const [isPromotionModalOpen, setIsPromotionModalOpen] = useState(false);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [transferStudents, setTransferStudents] = useState([]);
  const [allClasses, setAllClasses] = useState([]);
  const [isQRCodeModalOpen, setIsQRCodeModalOpen] = useState(false);
  const [isLoginAccessModalOpen, setIsLoginAccessModalOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dropdownIndex, setDropdownIndex] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState(null);
  const [showActionsDropdown, setShowActionsDropdown] = useState(false);
  const [filterText, setFilterText] = useState("");
  const [alert, setAlert] = useState({ type: "", message: "" });
  const [archiveConfirm, setArchiveConfirm] = useState({ isOpen: false, student: null });

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const toggleGenerateModal = () => {
    setAlert({ type: "", message: "" });
    if (isGenerateModalOpen) setInvoiceTargetStudent(null);
    setIsGenerateModalOpen(!isGenerateModalOpen);
  };

  const openSingleInvoiceModal = (student) => {
    handleCloseDropdown();
    setInvoiceTargetStudent(student);
    setAlert({ type: "", message: "" });
    setIsGenerateModalOpen(true);
  };

  const openStudentIDModal = (student) => {
    setSelectedStudent(student);
    setIsStudentIDModalOpen(true);
    handleCloseDropdown(); // Close dropdown when opening modal
  };

  const closeStudentIDModal = () => {
    setIsStudentIDModalOpen(false);
    setSelectedStudent(null);
  };

  const togglePromotionModal = async () => {
    if (!isPromotionModalOpen && classInfo?.next_class) {
      // Fetch next class data when opening modal
      const nextClassData = await fetchNextClassData(classInfo.next_class);
      setClassInfo((prev) => ({
        ...prev,
        nextClassData,
      }));
    }
    setIsPromotionModalOpen(!isPromotionModalOpen);
  };

  const toggleQRCodeModal = () => {
    setIsQRCodeModalOpen(!isQRCodeModalOpen);
  };

  const handlePromotionSuccess = () => {
    fetchStudents(); // Refresh the students list
    fetchClass(); // Refresh class info
  };

  const toggleTransferModal = (studentsToTransfer = []) => {
    if (!isTransferModalOpen) setTransferStudents(studentsToTransfer);
    setIsTransferModalOpen(!isTransferModalOpen);
  };

  // Full class list, used to work out which classes are ahead/behind this
  // one for skip-promotion and demotion.
  const fetchAllClasses = async () => {
    if (!schoolCode) return;
    try {
      const { data } = await userRequest.get(`classes?code=${schoolCode}`);
      setAllClasses(data.classes || []);
    } catch (error) {
      console.log("Error fetching classes:", error);
    }
  };

  // Fetch next class data for promotion modal
  const fetchNextClassData = async (nextClassId) => {
    if (!nextClassId) return null;

    try {
      const { data } = await userRequest.get(`classes/${schoolId}/${nextClassId}`);
      return data;
    } catch (error) {
      console.log("Error fetching next class:", error);
      return {
        _id: nextClassId,
        class_name: `Class ${nextClassId.slice(-4)}`,
        level_of_education: "primary",
      };
    }
  };

  useEffect(() => {
    if (classId && schoolId) {
      fetchStudents();
      fetchClass();
      fetchAllClasses();
      if (hasFinancial) fetchPaymentSummary();
    }
  }, [classId, schoolId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showActionsDropdown && !event.target.closest(".relative")) {
        setShowActionsDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showActionsDropdown]);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const { data } = await userRequest.get(`children/class/${classId}?limit=200`);
      setStudents(data.children);
      // Calculate active and inactive students
      const active = data.children.filter(
        (student) => student.academic_status === "active" && student.is_active,
      );
      const inactive = data.children.filter(
        (student) => student.academic_status !== "active" || !student.is_active,
      );

      setActiveStudents(active.length);
      setInactiveStudents(inactive.length);
      setLoading(false);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };
  const fetchClass = async () => {
    try {
      const { data } = await userRequest.get(`classes/${schoolId}/${classId}`);
      setClassInfo(data);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchPaymentSummary = async () => {
    const term = school?.settings?.current_term;
    const session = school?.settings?.current_session;
    if (!schoolId || !classId || !term || !session) return;
    try {
      const { data } = await userRequest.get(
        `schools/${schoolId}/payment-summary?class_id=${classId}&term=${term}&session=${encodeURIComponent(session)}`,
      );
      setPaymentSummary(data);
    } catch (error) {
      console.log(error);
    }
  };

  const ids = students?.map((child) => child._id);
  //console.log(ids);

  // Classes ahead/behind this one in the progression. Still used to label the
  // billing-class picker, but no longer limits where students can be moved:
  // any other class is a valid destination, since next_class holds a single id
  // and streamed schools (JSS 3 -> SS1 Science or SS1 Arts) can only wire one
  // branch, leaving the other unreachable by walking the chain.
  const classesAhead = getClassesAhead(allClasses, classId);
  const hasTransferTargets = (allClasses?.length || 0) > 1;

  const archiveHandler = async (studentId) => {
    try {
      setLoading(true);
      await userRequest.delete(`children/${studentId}`);
      await fetchStudents();
      setArchiveConfirm({ isOpen: false, student: null });
      displayAlert("success", "Student archived successfully!");
    } catch (error) {
      console.log(error);
      const errorMessage =
        error.response?.data?.message || "Failed to archive student. Please try again.";
      displayAlert("error", errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const openArchiveConfirm = (student) => {
    setArchiveConfirm({ isOpen: true, student });
    handleCloseDropdown();
  };

  const closeArchiveConfirm = () => {
    setArchiveConfirm({ isOpen: false, student: null });
  };

  const handleDropdownClick = (rowIndex, event) => {
    const rect = event.target.getBoundingClientRect();
    setDropdownIndex(dropdownIndex === rowIndex ? null : rowIndex);
    setDropdownPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
    });
  };

  const handleCloseDropdown = () => setDropdownIndex(null);

  const columns = [
    {
      name: "Admission Number",
      cell: (row) => row.registration_id,
    },
    {
      name: "Surname",
      cell: (row) => row.lastname,
    },
    {
      name: "First Name",
      cell: (row) => row.firstname,
    },
    {
      name: "Middle Name",
      cell: (row) => row.middlename,
    },
    {
      name: "Gender",
      cell: (row) => row.gender,
    },
    {
      name: "Account (collection)",
      selector: (row) => (
        <>
          {row.account ? (
            <div>
              <p>{row.account.accountNumber}</p>
              <p>{row.account.accountName}</p>
            </div>
          ) : (
            <p>Account not assigned</p>
          )}
        </>
      ),
    },
    {
      name: "Status",
      selector: (row) => (
        <div>
          {row.academic_status ? (
            <StatusBadge status="active" />
          ) : (
            <StatusBadge status="inactive" />
          )}
        </div>
      ),
    },
    {
      // Visually hidden column
      name: "Account (collection)",
      cell: (row) =>
        row.account ? `${row.account.accountName} (${row.account.accountNumber})` : "No account",
      hidden: true,
    },
    {
      cell: (row, rowIndex) => (
        <div className="relative">
          <button
            className="p-2 text-gray-600 hover:text-gray-900"
            onClick={(e) => handleDropdownClick(rowIndex, e)}
          >
            <FiMoreHorizontal className="text-2xl" />
          </button>
          <TableDropdown
            isVisible={dropdownIndex === rowIndex}
            position={dropdownPosition}
            options={[
              {
                label: "View Student ID",
                icon: <FiCreditCard className="text-indigo-500 text-xl" />,
                onClick: () => openStudentIDModal(row),
              },
              {
                label: "Students Profile",
                icon: <FiUser className="text-blue-500 text-xl" />,
                onClick: () => {
                  history.push(`/students/view/${row._id}/${classId}`, {
                    student: [row.lastname, row.firstname, row.middlename]
                      .filter(Boolean)
                      .join(" "),
                  });
                },
              },
              {
                label: "Edit Student",
                icon: <FiEdit2 className="text-gray-500 text-xl" />,
                onClick: () => {
                  history.push(`/update-student/${row._id}`, { student: row });
                },
              },
              {
                label: "Create Account",
                icon: <FiPlusCircle className="text-green-500 text-xl" />,
                onClick: () => handleCreateSingleAccount(row),
                condition: hasFinancial && !row.account,
              },
              {
                label: "Generate Invoice",
                icon: <FiFileText className="text-indigo-500 text-xl" />,
                onClick: () => openSingleInvoiceModal(row),
                condition: hasFinancial && !isAcademicAdmin,
              },
              {
                label: "Promote Student",
                icon: <MdArrowCircleUp className="text-2xl" />,
                onClick: () => {
                  // Individual student promotion/graduation
                  setSelectedStudent(row);
                  setIsPromotionModalOpen(true);
                  handleCloseDropdown();
                },
                condition:
                  row.is_active &&
                  row.academic_status === "active" &&
                  (classInfo?.is_last_class || classInfo?.next_class),
              },
              {
                label: "Skip Promote / Demote",
                icon: <MdSwapVert className="text-2xl text-purple-500" />,
                onClick: () => {
                  toggleTransferModal([row]);
                  handleCloseDropdown();
                },
                condition: row.is_active && row.academic_status === "active" && hasTransferTargets,
              },
              {
                label: "Archive Student",
                icon: <FiTrash2 className="text-amber-500 text-xl" />,
                onClick: () => openArchiveConfirm(row),
                className: "text-amber-600 hover:text-amber-800",
              },
              {
                label: isBypassActive(
                  row.bypass_payment_threshold,
                  school?.settings?.current_term,
                  school?.settings?.current_session,
                )
                  ? "Revoke Login Exemption"
                  : "Allow Login (Exemption)",
                icon: (
                  <FiShield
                    className={
                      isBypassActive(
                        row.bypass_payment_threshold,
                        school?.settings?.current_term,
                        school?.settings?.current_session,
                      )
                        ? "text-amber-500 text-xl"
                        : "text-indigo-500 text-xl"
                    }
                  />
                ),
                onClick: () => handleToggleBypassThreshold(row),
              },
            ]}
            onClose={handleCloseDropdown}
          />
        </div>
      ),
      width: "120px",
    },
  ];

  const filteredData = students
    ?.filter(
      (item) =>
        item.firstname?.toLowerCase().includes(filterText.toLowerCase()) ||
        "" ||
        item.lastname?.toLowerCase().includes(filterText.toLowerCase()) ||
        "",
    )
    .sort((a, b) => {
      // Sort by lastname alphabetically
      const lastNameA = (a.lastname || "").toLowerCase();
      const lastNameB = (b.lastname || "").toLowerCase();
      return lastNameA.localeCompare(lastNameB);
    });

  // Bulk assign accounts
  const accountHandler = async (e) => {
    e.preventDefault();
    const studentsWithoutAccount = students?.filter((s) => !s.account);
    if (!studentsWithoutAccount?.length) {
      displayAlert("info", "All students already have accounts assigned.");
      return;
    }

    setLoading(true);
    try {
      await userRequest.post("/banking/virtual-accounts/student/bulk", {
        schoolId,
        students: studentsWithoutAccount.map((s) => ({
          studentId: s._id,
          studentName: `${s.firstname} ${s.lastname}`.trim(),
        })),
      });
      await fetchStudents();
      displayAlert(
        "success",
        `Accounts created for ${studentsWithoutAccount.length} student${studentsWithoutAccount.length > 1 ? "s" : ""}.`,
      );
    } catch (error) {
      console.log(error);
      displayAlert(
        "error",
        error.response?.data?.message || "Failed to create accounts. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  // Single student account creation from dropdown
  const handleCreateSingleAccount = async (student) => {
    handleCloseDropdown();
    setLoading(true);
    try {
      await userRequest.post("/banking/virtual-accounts/student", {
        schoolId,
        studentId: student._id,
        studentName: `${student.firstname} ${student.lastname}`.trim(),
      });
      displayAlert("success", `Account created for ${student.firstname} ${student.lastname}.`);
      await fetchStudents();
    } catch (error) {
      displayAlert(
        "error",
        error.response?.data?.message ||
          `Failed to create account for ${student.firstname} ${student.lastname}.`,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleToggleBypassThreshold = async (student) => {
    handleCloseDropdown();
    const currentTerm = school?.settings?.current_term;
    const currentSession = school?.settings?.current_session;
    const isActive = isBypassActive(student.bypass_payment_threshold, currentTerm, currentSession);
    const newValue = !isActive;

    // Optimistic local patch instead of refetching the whole class roster (which
    // can be up to 200 students) for a single field change on one student.
    const bypassPatch = newValue
      ? { enabled: true, term: currentTerm ? Number(currentTerm) : undefined, session: currentSession }
      : { enabled: false };
    setStudents((prev) =>
      prev.map((s) => (s._id === student._id ? { ...s, bypass_payment_threshold: bypassPatch } : s)),
    );

    try {
      await userRequest.post(`/children/schools/${schoolId}/bulk-bypass-payment-threshold`, {
        student_ids: [student._id],
        enabled: newValue,
        term: currentTerm ? Number(currentTerm) : undefined,
        session: currentSession,
      });
      displayAlert(
        "success",
        `${student.firstname}'s login exemption has been ${
          newValue
            ? `granted for Term ${currentTerm || 1} (${currentSession || "Current"})`
            : "revoked"
        }.`,
      );
    } catch (error) {
      displayAlert("error", "Failed to update student login exemption.");
      // Revert on failure by re-fetching, since we can't know the true prior state.
      await fetchStudents();
    }
  };

  const handleGenerateInvoices = async (payload, targetStudent = null) => {
    try {
      setLoading(true);
      setAlert({ type: "", message: "" });

      const body = {
        add_ons: payload.addOns,
        transport_add_on: payload.transportAddOn || "",
        ...(payload.session && { session: payload.session }),
        ...(payload.term && { term: payload.term }),
        ...(payload.billingClassId && { billing_class_id: payload.billingClassId }),
        ...(payload.arrears && { arrears: payload.arrears }),
        notify_parents: payload.notifyParents,
      };

      let response;
      if (targetStudent) {
        response = await userRequest.post(
          `/children/${schoolId}/create-payment-for-specific-children/${classId}`,
          { ...body, children: [targetStudent._id] },
        );
      } else {
        response = await userRequest.post(
          `/children/${schoolId}/create-payment-for-all-children/${classId}`,
          body,
        );
      }

      if (response.status >= 200 && response.status < 300) {
        displayAlert(
          "success",
          targetStudent
            ? `Invoice generated for ${targetStudent.firstname} ${targetStudent.lastname}.`
            : "Invoices generated successfully!",
        );
        setTimeout(() => toggleGenerateModal(), 2000);
      } else {
        throw new Error(response.data || "Failed to generate invoices");
      }
    } catch (error) {
      console.log(error);
      const errorMessage = error.response?.data || "Failed to generate invoices. Please try again.";
      displayAlert("error", errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const displayAlert = (type, message) => {
    setAlert({ type, message });
  };

  return (
    <>
      <div className="px-10 pt-6">
        <BackButton history={history} />
      </div>
      <div className="px-10 pt-5">
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center mb-4 gap-4">
          <div className="flex-shrink-0">
            <Header category="Students" title={classInfo?.class_name || "Loading..."} />
          </div>
          <div className="flex-shrink-0">
            {/* Primary Actions - Desktop Layout */}
            <div className="hidden lg:flex lg:items-center lg:space-x-3">
              {/* Add Student Button - Primary */}
              <button
                type="button"
                onClick={toggleModal}
                className="group inline-flex items-center px-4 py-2.5 bg-[#3F3DA5] hover:bg-blue-700 text-white font-medium rounded-full shadow-sm hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                <svg
                  className="w-4 h-4 mr-2 group-hover:scale-110 transition-transform duration-200"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Add Student
              </button>

              {/* Secondary Actions Dropdown */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowActionsDropdown(!showActionsDropdown)}
                  className="inline-flex items-center px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-full border border-gray-300 shadow-sm hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                >
                  Bulk Operations
                  <svg
                    className="w-4 h-4 ml-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                {/* Dropdown Menu */}
                {showActionsDropdown && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                    <div className="py-1">
                      <button
                        onClick={() => {
                          history.push("/archived-students");
                          setShowActionsDropdown(false);
                        }}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                      >
                        <MdSearch className="w-4 h-4 mr-3 text-amber-500" />
                        Archived Students
                      </button>
                      <button
                        onClick={() => {
                          setIsLoginAccessModalOpen(true);
                          setShowActionsDropdown(false);
                        }}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                      >
                        <FiShield className="w-4 h-4 mr-3 text-indigo-600" />
                        Login & CBT Access
                      </button>
                      <button
                        onClick={() => {
                          toggleQRCodeModal();
                          setShowActionsDropdown(false);
                        }}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                      >
                        <FiSquare className="w-4 h-4 mr-3 text-green-600" />
                        Generate ID Cards
                      </button>
                      {hasTransferTargets && (
                        <button
                          onClick={() => {
                            toggleTransferModal(students);
                            setShowActionsDropdown(false);
                          }}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                        >
                          <MdSwapVert className="w-4 h-4 mr-3 text-purple-600" />
                          Skip Promote / Demote
                        </button>
                      )}
                      {hasFinancial && !isAcademicAdmin && (
                        <button
                          onClick={() => {
                            toggleGenerateModal();
                            setShowActionsDropdown(false);
                          }}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                        >
                          <svg
                            className="w-4 h-4 mr-3 text-purple-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                          Generate Invoices
                        </button>
                      )}
                      {hasFinancial &&
                        (loading ? (
                          <div className="flex items-center px-4 py-2 text-sm text-gray-500">
                            <div className="animate-spin rounded-full h-3 w-3 border-2 border-gray-300 border-t-blue-500 mr-3"></div>
                            Assigning...
                          </div>
                        ) : (
                          <button
                            onClick={accountHandler}
                            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                          >
                            <svg
                              className="w-4 h-4 mr-3 text-indigo-600"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                              />
                            </svg>
                            Assign Accounts
                          </button>
                        ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Promote Students Button - Special Action */}
              <button
                type="button"
                onClick={togglePromotionModal}
                disabled={classInfo?.is_last_class ? false : !classInfo?.next_class}
                className="inline-flex items-center px-4 py-2.5 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed text-white font-medium rounded-full shadow-sm hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
                title={
                  !classInfo?.next_class && !classInfo?.is_last_class
                    ? "No next class configured for promotion"
                    : "Promote students to next class"
                }
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 11l5-5m0 0l5 5m-5-5v12"
                  />
                </svg>
                Promote Students
              </button>
            </div>

            {/* Mobile Layout - Stacked */}
            <div className="lg:hidden space-y-3">
              {/* Primary Action */}
              <button
                type="button"
                onClick={toggleModal}
                className="w-full group inline-flex items-center justify-center px-4 py-3 bg-[#3F3DA5] hover:bg-blue-700 text-white font-medium rounded-full shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                <svg
                  className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform duration-200"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Add New Student
              </button>

              {/* Secondary Actions Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={toggleQRCodeModal}
                  className="flex items-center justify-center px-4 py-3 bg-green-50 hover:bg-green-100 border border-green-200 hover:border-green-300 text-green-700 font-medium rounded-full shadow-sm hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                >
                  <FiSquare className="w-5 h-5 mr-2" />
                  ID Cards
                </button>

                <button
                  type="button"
                  onClick={() => setIsLoginAccessModalOpen(true)}
                  className="flex items-center justify-center px-4 py-3 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 hover:border-indigo-300 text-indigo-700 font-medium rounded-full shadow-sm hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >
                  <FiShield className="w-5 h-5 mr-2 text-indigo-600" />
                  Login Access
                </button>

                {hasFinancial && (
                  <button
                    type="button"
                    onClick={toggleGenerateModal}
                    className="flex items-center justify-center px-4 py-3 bg-purple-50 hover:bg-purple-100 border border-purple-200 hover:border-purple-300 text-purple-700 font-medium rounded-full shadow-sm hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
                  >
                    <svg
                      className="w-5 h-5 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    Invoices
                  </button>
                )}
                {hasFinancial &&
                  (loading ? (
                    <div className="col-span-2 flex items-center justify-center px-4 py-3 bg-gray-50 border border-gray-200 text-gray-500 font-medium rounded-lg">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-300 border-t-blue-500 mr-2"></div>
                      Assigning Accounts...
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={accountHandler}
                      className="flex items-center justify-center px-4 py-3 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 hover:border-indigo-300 text-indigo-700 font-medium rounded-lg shadow-sm hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    >
                      <svg
                        className="w-5 h-5 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                        />
                      </svg>
                      Assign Accounts
                    </button>
                  ))}

                <button
                  type="button"
                  onClick={togglePromotionModal}
                  disabled={classInfo?.is_last_class ? false : !classInfo?.next_class}
                  className="flex items-center justify-center px-4 py-3 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed text-white font-medium rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
                  title={
                    !classInfo?.next_class && !classInfo?.is_last_class
                      ? "No next class configured for promotion"
                      : "Promote students"
                  }
                >
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 11l5-5m0 0l5 5m-5-5v12"
                    />
                  </svg>
                  Promote
                </button>

                {hasTransferTargets && (
                  <button
                    type="button"
                    onClick={() => toggleTransferModal(students)}
                    className="flex items-center justify-center px-4 py-3 bg-purple-50 hover:bg-purple-100 border border-purple-200 hover:border-purple-300 text-purple-700 font-medium rounded-lg shadow-sm hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
                  >
                    <MdSwapVert className="w-5 h-5 mr-2" />
                    Skip / Demote
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-2 m-2 md:m-2 mt-5 p-2 md:p-5">
        <div className="bg-white border-2 border-gray-200 dark:text-gray-200 dark:bg-secondary-dark-bg h-34 rounded-3xl p-8 pt-9 m-3">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-gray-400 text-sm">Total Student</p>
              <p className="text-2xl font-semibold">{students.length}</p>
            </div>
            <button
              type="button"
              className="bg-[#3371E8] text-white text-2xl opacity-90 text-white hover:drop-shadow-xl rounded-xl py-4 px-4"
            >
              <img src={Icons.userMultiple} alt="Wallet Icon" />
            </button>
          </div>
        </div>

        <div className="bg-white border-2 border-gray-200 dark:text-gray-200 dark:bg-secondary-dark-bg h-34 rounded-3xl p-8 pt-9 m-3">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-gray-400 text-sm">Active Students</p>
              <p className="text-2xl font-semibold">{activeStudents}</p>
            </div>
            <button
              type="button"
              className="bg-[#41D3BD] text-white text-2xl opacity-90 text-white hover:drop-shadow-xl rounded-xl py-4 px-4"
            >
              <img src={Icons.coupon} alt="Payout Icon" />
            </button>
          </div>
        </div>

        <div className="bg-white border-2 border-gray-200 dark:text-gray-200 dark:bg-secondary-dark-bg h-34 rounded-3xl p-8 pt-9 m-3">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-gray-400 text-sm">Inactive Students</p>
              <p className="text-2xl font-semibold">{inactiveStudents}</p>
            </div>
            <button
              type="button"
              className="bg-[#E26E6A] text-white text-2xl opacity-90 text-white hover:drop-shadow-xl rounded-xl py-4 px-4"
            >
              <img src={Icons.taskDaily} alt="Cashflow Icon" />
            </button>
          </div>
        </div>
      </div>

      {/* Payment Summary Banner */}
      {hasFinancial && paymentSummary !== null && (
        <div
          className={`mx-2 md:mx-10 mb-4 rounded-2xl border px-5 py-4
        ${
          paymentSummary.totalAmount > 0
            ? "bg-green-50 border-green-200"
            : "bg-amber-50 border-amber-200"
        }`}
        >
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            {/* Status indicator */}
            <div className="flex items-center gap-2 shrink-0">
              {paymentSummary.totalAmount > 0 ? (
                <FiCheckCircle className="text-green-600 text-xl shrink-0" />
              ) : (
                <FiAlertCircle className="text-amber-500 text-xl shrink-0" />
              )}
              <div>
                <p
                  className={`text-sm font-semibold ${paymentSummary.totalAmount > 0 ? "text-green-700" : "text-amber-700"}`}
                >
                  {paymentSummary.totalAmount > 0 ? "Invoices Generated" : "No Invoices Yet"}
                </p>
                <p className="text-xs text-gray-500">
                  {school?.settings?.current_term ? `Term ${school.settings.current_term}` : ""} ·{" "}
                  {school?.settings?.current_session}
                </p>
              </div>
            </div>

            {/* Divider */}
            {paymentSummary.totalAmount > 0 && (
              <div className="hidden sm:block w-px h-10 bg-gray-200" />
            )}

            {/* Metrics */}
            {paymentSummary.totalAmount > 0 && (
              <div className="flex flex-wrap gap-x-6 gap-y-2">
                <div>
                  <p className="text-xs text-gray-400">Total Billed</p>
                  <p className="text-sm font-semibold text-gray-800">
                    {formatCurrency(paymentSummary.totalAmount)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Amount Paid</p>
                  <p className="text-sm font-semibold text-green-600">
                    {formatCurrency(paymentSummary.totalAmountPaid)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Outstanding</p>
                  <p className="text-sm font-semibold text-red-500">
                    {formatCurrency(paymentSummary.totalOutstandingAmount)}
                  </p>
                </div>
                {paymentSummary.discountAmount > 0 && (
                  <div>
                    <p className="text-xs text-gray-400">Discounts</p>
                    <p className="text-sm font-semibold text-indigo-500">
                      {formatCurrency(paymentSummary.discountAmount)}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* CTA when no invoices */}
            {paymentSummary.totalAmount === 0 && (
              <p className="text-xs text-amber-600 sm:ml-2">
                Use <span className="font-semibold">Bulk Operations → Generate Invoices</span> to
                create invoices for this class.
              </p>
            )}
          </div>
        </div>
      )}

      <div className="m-2 md:m-10 mt-24 p-4 md:p-10 bg-white rounded-3xl dark:bg-secondary-dark-bg">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-lg font-semibold w-44">Students Details</h1>
          <div className="flex justify-center md:justify-end w-full">
            <form className="max-w-lg">
              <div className="relative">
                <div className="absolute inset-y-0 start-0 flex items-center pl-4 pointer-events-none">
                  <MdSearch />
                </div>
                <input
                  type="text"
                  id="email-address-icon"
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-md rounded-full focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 p-2.5"
                  placeholder="Search"
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                />
              </div>
            </form>
            <button
              type="button"
              onClick={() =>
                exportStudentsToExcel(
                  filteredData,
                  `${classInfo?.class_name || "Students"}_export.xlsx`,
                )
              }
              className="inline-flex justify-center py-2 px-6 mx-2 mb-2 border border-transparent shadow-sm text-md font-medium rounded-full text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2"
            >
              Export to Excel
            </button>
          </div>
        </div>
        <div className="border rounded-3xl">
          <DataTable
            data={filteredData}
            columns={columns.filter((col) => !col.hidden)}
            getRowId={(row) => row._id}
            pagination
            paginationComponent={CustomPagination}
            paginationRowsPerPageOptions={[10, 20, 30, 50]}
            customStyles={customStyles}
            progressPending={loading}
            progressComponent={<SkeletonTable rows={5} columns={6} />}
            highlightOnHover
            striped
          />
        </div>
        {isModalOpen && (
          <AddStudentDetailsModal
            closeModal={toggleModal}
            onStudentCreated={() => {
              fetchStudents();
              refreshSubscription();
            }}
            schoolId={schoolId}
            schoolCode={schoolCode}
            classId={classId}
          />
        )}
        {isGenerateModalOpen && (
          <GenerateInvoicesModal
            closeModal={toggleGenerateModal}
            schoolId={schoolId}
            schoolCode={schoolCode}
            classId={classId}
            alert={alert}
            student={invoiceTargetStudent}
            currentSession={school?.settings?.current_session}
            currentTerm={school?.settings?.current_term}
            classesAhead={classesAhead}
            submitting={loading}
            onGenerate={(payload) => handleGenerateInvoices(payload, invoiceTargetStudent)}
          />
        )}

        {/* Student ID Modal */}
        {isStudentIDModalOpen && selectedStudent && (
          <StudentIDModal
            isOpen={isStudentIDModalOpen}
            onClose={closeStudentIDModal}
            student={selectedStudent}
            school={school}
            classInfo={classInfo}
          />
        )}

        {/* Student Promotion Modal */}
        {isPromotionModalOpen && (
          <StudentPromotionModal
            isOpen={isPromotionModalOpen}
            onClose={togglePromotionModal}
            students={students}
            classInfo={classInfo}
            schoolCode={schoolCode}
            onPromotionSuccess={handlePromotionSuccess}
          />
        )}

        {/* Skip Promotion / Demotion Modal */}
        {isTransferModalOpen && (
          <ClassTransferModal
            isOpen={isTransferModalOpen}
            onClose={() => toggleTransferModal()}
            students={transferStudents}
            classInfo={classInfo}
            allClasses={allClasses}
            schoolCode={schoolCode}
            onTransferSuccess={handlePromotionSuccess}
          />
        )}

        {/* Generate QR Code Modal */}
        {isQRCodeModalOpen && (
          <GenerateQRCodeModal
            isOpen={isQRCodeModalOpen}
            onClose={toggleQRCodeModal}
            students={students}
            schoolId={schoolId}
            classId={classId}
            school={school}
            classInfo={classInfo}
          />
        )}

        {/* Student Login & CBT Access Modal */}
        {isLoginAccessModalOpen && (
          <StudentLoginAccessModal
            isOpen={isLoginAccessModalOpen}
            onClose={() => {
              setIsLoginAccessModalOpen(false);
              fetchStudents();
            }}
            school={school}
            initialClassId={classId}
          />
        )}

        {/* Archive Confirmation Modal */}
        {archiveConfirm.isOpen && archiveConfirm.student && (
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-md flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-xl">
              <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-amber-100 rounded-full">
                <svg
                  className="w-6 h-6 text-amber-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
                  />
                </svg>
              </div>

              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                Archive Student
              </h3>

              <p className="text-sm text-gray-600 text-center mb-2">
                Are you sure you want to archive{" "}
                <span className="font-semibold">
                  {archiveConfirm.student.firstname} {archiveConfirm.student.lastname}
                </span>
                ?
              </p>
              <p className="text-sm text-amber-700 text-center bg-amber-50 rounded-lg px-4 py-2 mb-6">
                Archived students can be restored at any time from the{" "}
                <span className="font-semibold">Archived Students</span> page.
              </p>

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={closeArchiveConfirm}
                  disabled={loading}
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => archiveHandler(archiveConfirm.student._id)}
                  disabled={loading}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-amber-500 border border-transparent rounded-lg hover:bg-amber-600 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Archiving...
                    </div>
                  ) : (
                    "Archive Student"
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
export default Students;
